/**
 * Video Listener
 *
 * Registers DOM and media element listeners for video picker behavior.
 */

import { VideoHandler } from "./video-handler";
import { videoForm } from "./video-form";

type VideoEventCallback = (this: HTMLVideoElement, event: Event) => unknown;
type FilesSelectedCallback = (files: File[]) => Promise<void> | void;

class VideoListener {
    public static setupTileRemoveButtonListener(removeBtn: HTMLButtonElement, onRemove: (event: Event) => void): void {
        removeBtn.addEventListener("click", onRemove);
    }

    public static setupThumbnailListeners(
        video: HTMLVideoElement,
        onMetadataLoaded: VideoEventCallback,
        onSeeked: VideoEventCallback,
        onError: VideoEventCallback,
    ): void {
        video.addEventListener("loadedmetadata", onMetadataLoaded);
        video.addEventListener("seeked", onSeeked);
        video.addEventListener("error", onError);
    }

    public static setupThumbnailGenListeners(
        video: HTMLVideoElement,
        onMetadata: () => void,
        onCapture: () => void,
        onError: () => void,
    ): void {
        video.addEventListener("loadedmetadata", onMetadata);
        video.addEventListener("seeked", onCapture);
        video.addEventListener("loadeddata", onCapture);
        video.addEventListener("canplay", onCapture);
        video.addEventListener("error", onError);
    }

    public static setupFileInputListener(fileInput: HTMLInputElement, onFilesSelected: FilesSelectedCallback): void {
        fileInput.addEventListener("change", async () => {
            const files = Array.from(fileInput.files || []);
            if (files.length === 0) return;
            await onFilesSelected(files);
        });
    }

    public static setupAddButtonListener(addBtn: HTMLButtonElement, fileInput: HTMLInputElement): void {
        addBtn.addEventListener("click", () => {
            fileInput.click();
        });
    }

    public static setupCreateModeClickListeners(handler: InstanceType<typeof VideoHandler>): void {
        handler.container.addEventListener("click", (event: Event) => {
            const target = videoForm.getEventTarget(event);
            if (!(target instanceof HTMLElement)) return;

            if (videoForm.getClosestRemoveButton(target)) {
                return;
            }
        });
    }

    public static setupEditModeClickListeners(handler: InstanceType<typeof VideoHandler>): void {
        handler.container.addEventListener("click", (event: Event) => {
            const target = videoForm.getEventTarget(event);
            if (!(target instanceof HTMLElement)) return;

            const removeExistingBtn = videoForm.getClosestRemoveButton(target);
            if (removeExistingBtn) {
                VideoHandler.handleExistingVideoRemoval(handler, event);
            }
        });
    }
}

export { VideoListener };
